import { BehaviorSubject, finalize, Observable } from "rxjs";
import { API } from "../constants";
import * as CryptoJS from 'crypto-js';
import { BaseResponseModel } from "../models"
import { UserModel } from "../models/user.model";
import { Inject, Injectable } from "@angular/core";
import { UserLoginModel } from "app/account/model";
import { UserAuthInterface } from "./user-auth.interface";
import { ResetPasswordModel } from "app/account/model/reset-pwd-model";
import { PullProductCreateModel } from "../models/pull-products.create.model";
import { GetDirectoryMetaDataListModel } from "../models/directory-meta-data-list.model";
import { HTTP_SERVICE, HttpInterface } from "./http.interface";

@Injectable()
export class UserAuthService implements UserAuthInterface {

    //#region Constants
    private AUTH_USER_KEY_NAME = "kisna.cdb.auth.usp";
    private REMEMBER_CREDENTIALS_KEY = 'kisna.cdb.remember.credentials';
    private DEVICE_KEY_NAME = 'kisna.cdb.device.key';
    private REMEMBER_EXPIRY_DAYS = 7; 
    //#endregion

    //#region Constructor

    constructor(@Inject(HTTP_SERVICE) private _httpInterface: HttpInterface) { }

    //#endregion

    //#region Properties

    private _currentAuthUser: UserModel;


    get authenticatedUserPermissions(): string[] {
        let authUser = this.authenticatedUser;
        return authUser?.permissions ?? Array<string>();
    }


    get authenticatedUser(): UserModel {
        if (this._currentAuthUser) {
            return this._currentAuthUser;
        }
        const data = localStorage.getItem(this.AUTH_USER_KEY_NAME) || sessionStorage.getItem(this.AUTH_USER_KEY_NAME);
        if (!data) return null;
        this._currentAuthUser = new UserModel();
        Object.assign(this._currentAuthUser, JSON.parse(data));
        return this._currentAuthUser;
    }

    //#endregion


    //#region Methods

    login(Code: string, Password: string): Observable<BaseResponseModel<UserModel>> {
        return this._httpInterface.post<BaseResponseModel<UserModel>>(API.USER_LOGI_IN, { UserName: Code, Password: Password });
    }

    sendPasswordResetLink(email: string): Observable<BaseResponseModel<string>> {
        return this._httpInterface.post<BaseResponseModel<string>>(API.USER_FORGOT_PASSWORD, { email: email });
    }

    resetUserPassword(model: ResetPasswordModel): Observable<BaseResponseModel<UserModel>> {
        return this._httpInterface.post<BaseResponseModel<UserModel>>(API.USER_RESET_PASSWORD, model);
    }


    setAuthenticatedUser(user: UserModel, rememberMe: boolean): void {
        if (!user) {
            throw 'User authentication is null';
        }

        this._currentAuthUser = user;
        this.userData.next(user);
        const storage = rememberMe ? localStorage : sessionStorage;
        storage.setItem(this.AUTH_USER_KEY_NAME, JSON.stringify(this._currentAuthUser));
    }

    private getDeviceKey(): string {
        let deviceKey = localStorage.getItem(this.DEVICE_KEY_NAME);
        if (!deviceKey) {
            deviceKey = crypto.randomUUID();
            localStorage.setItem(this.DEVICE_KEY_NAME, deviceKey);
        }
        return deviceKey;
    }

    setRememberCredentials(model: UserLoginModel): void {
        if (!model) {
            throw 'User credentials are null';
        }
        const payload = {
            Code: model.Code,
            Password: model.Password,
            expiresAt: Date.now() + this.REMEMBER_EXPIRY_DAYS * 24 * 60 * 60 * 1000
        };
        const encryptedData = CryptoJS.AES.encrypt(
            JSON.stringify(payload),
            this.getDeviceKey()
        ).toString();
        localStorage.setItem(this.REMEMBER_CREDENTIALS_KEY, encryptedData);
    }

    getRememberCredentials(): UserLoginModel | null {
        const encryptedData = localStorage.getItem(this.REMEMBER_CREDENTIALS_KEY);

        if (!encryptedData) return null;

        try {
            const bytes = CryptoJS.AES.decrypt(encryptedData, this.getDeviceKey());
            const decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));

            if (!decryptedData?.expiresAt || Date.now() > decryptedData.expiresAt) {
                this.clearRememberCredentials();
                return null;
            }

            return { Code: decryptedData.Code, Password: decryptedData.Password } as UserLoginModel;
        } catch (error) {
            console.error('Decrypt error', error);
            this.clearRememberCredentials();
            return null;
        }
    }


    clearRememberCredentials(): void {
        localStorage.removeItem(this.REMEMBER_CREDENTIALS_KEY);
    }


    hasPermission(permissionName: string): boolean {
        if (!this._currentAuthUser || !this._currentAuthUser.permissions) {
            return false;
        }
        return this._currentAuthUser.permissions.some(item => item === permissionName);
    }

    clearSession(): void {
        this._currentAuthUser = null;
        localStorage.removeItem(this.AUTH_USER_KEY_NAME);
        sessionStorage.removeItem(this.AUTH_USER_KEY_NAME);
    }

    logoutUser() {
        return this._httpInterface.post(API.LOGOUT_USER, {}).pipe(
            finalize(() => this.clearSession())
        );
    }



    getDirecotryMetaDataList(): Observable<BaseResponseModel<GetDirectoryMetaDataListModel>> {
        return this._httpInterface.get(API.GET_DIRECTORY_META_DATA_LIST);
    }

    getMaterialTypeListData(): Observable<BaseResponseModel<any>> {
        return this._httpInterface.get(API.GET_MATERIAL_TYPE_LIST);
    }

    pullProductData(saerchModel: PullProductCreateModel): Observable<BaseResponseModel<string>> {
        return this._httpInterface.post(API.PULL_PRODUCTS, saerchModel)
    }


    // to store user data

    private userData = new BehaviorSubject<any | null>(null);
    userData$ = this.userData.asObservable();

    setUserData(data: any | null) {
        this.userData.next(data);
    }

    getUserData(): any | null {
        return this.userData.value ?? this.authenticatedUser;
    }
    //#endregion

    //#region JWT helpers

    private decodeToken(token: string): any | null {
        try {
            const payload = token.split('.')[1];
            // base64url -> base64
            const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join('')
            );
            return JSON.parse(jsonPayload);
        } catch (error) {
            console.error('Token decode error', error);
            return null;
        }
    }

    isTokenExpired(token?: string): boolean {
        const t = token ?? this.authenticatedUser?.token;
        if (!t) return true;

        const decoded = this.decodeToken(t);
        if (!decoded?.exp) return true;

        // exp is in seconds, Date.now() is in ms
        return Date.now() >= decoded.exp * 1000;
    }

    //#endregion
}